import { Static, Type } from '@sinclair/typebox';
import {
  GoogleReviewStatus,
  GOOGLE_REVIEW_STATUS_TRANSLATE_KEY,
} from './translation_namespace/google_review_namespace';
import { FilterConfig } from './filter/utils';
import { SortConfig, SortDirection, sortNullsLast } from './filter/sort';

/** Préfixe de traduction (namespace DYNAMIC_FORM) des libellés de note dans le filtre avis. */
export const GOOGLE_REVIEW_RATING_TRANSLATE_KEY = 'GOOGLE_REVIEW_RATING_';

/** Coerce une valeur de filtre (scalaire ou tableau, repeat ou comma) en entiers valides. */
const toReviewFilterIds = (value: unknown): number[] =>
  (Array.isArray(value) ? value : String(value ?? '').split(','))
    .map((v): number => Number(v))
    .filter((n): boolean => Number.isInteger(n) && n > 0);

/** Filtres de la page e-réputation, consommés par `<DynamicFilter>`. */
export const GOOGLE_REVIEW_FILTERS: FilterConfig = {
  google_location_id: {
    type: 'establishment_place_selector',
    label: 'FILTER_TITLE_GOOGLE_REVIEW_LOCATION',
    placeholder: 'FILTER_PLACEHOLDER_GOOGLE_REVIEW_LOCATION',
    primary: true,
    multiple: true,
    // Le type `establishment_place_selector` n'est pas géré par le switch générique de
    // `generatePrismaFilters` : on mappe explicitement vers la colonne FK `google_location_id`.
    customHandler: (value: unknown): Record<string, unknown> => {
      const ids = toReviewFilterIds(value);
      return ids.length ? { google_location_id: { in: ids } } : {};
    },
  },
  status: {
    type: 'enum',
    label: 'FILTER_TITLE_GOOGLE_REVIEW_STATUS',
    placeholder: 'FILTER_PLACEHOLDER_GOOGLE_REVIEW_STATUS',
    enum_values: Object.values(GoogleReviewStatus),
    translate_key: GOOGLE_REVIEW_STATUS_TRANSLATE_KEY,
    primary: true,
    multiple: true,
  },
  star_rating: {
    type: 'enum',
    label: 'FILTER_TITLE_GOOGLE_REVIEW_RATING',
    placeholder: 'FILTER_PLACEHOLDER_GOOGLE_REVIEW_RATING',
    enum_values: ['5', '4', '3', '2', '1'],
    translate_key: GOOGLE_REVIEW_RATING_TRANSLATE_KEY,
    primary: true,
    multiple: true,
    // `star_rating` est une colonne entière : le handler `enum` générique produirait des
    // strings (`{ in: ['5'] }`) → erreur Prisma. On force la coercition en entiers ici.
    customHandler: (value: unknown): Record<string, unknown> => {
      const ratings = toReviewFilterIds(value).filter((n): boolean => n >= 1 && n <= 5);
      return ratings.length ? { star_rating: { in: ratings } } : {};
    },
  },
};

/**
 * Tri de la page e-réputation (`/marketing/reviews`). Défaut : date de l'avis décroissante.
 * Changement volontaire vs l'ancien `{ review_created_at: 'desc' }` : les avis sans date passent
 * en dernier (Postgres les plaçait en tête en décroissant) et le tiebreaker `google_review_id`
 * rend la pagination déterministe.
 */
export const GOOGLE_REVIEW_SORTS: SortConfig = {
  fields: {
    review_created_at: {
      label: 'SORT_FIELD_REVIEW_DATE',
      value_type: 'date',
      default_direction: SortDirection.DESC,
      order_by: (direction: SortDirection): Record<string, unknown>[] => [
        { review_created_at: sortNullsLast(direction) },
      ],
      api_description:
        "Date de publication de l'avis sur Google. C'est l'ordre par défaut, du plus récent au plus ancien ; les avis sans date sont toujours placés en dernier.",
    },
    star_rating: {
      label: 'SORT_FIELD_RATING',
      value_type: 'rating',
      default_direction: SortDirection.DESC,
      order_by: (direction: SortDirection): Record<string, unknown>[] => [
        { star_rating: direction },
        // À note égale, les avis les plus récents d'abord (quel que soit le sens de la note).
        { review_created_at: sortNullsLast(SortDirection.DESC) },
      ],
      api_description:
        "Note de l'avis (1 à 5 étoiles). Par défaut les meilleures notes d'abord ; à note égale, les avis les plus récents en premier.",
    },
  },
  default_field: 'review_created_at',
  tiebreaker: 'google_review_id',
};

export const GoogleReviewResponseSchema = Type.Object({
  google_review_id: Type.Number(),
  google_location_id: Type.Number(),
  location_title: Type.String(),
  star_rating: Type.Number(),
  comment: Type.Optional(Type.String()),
  reviewer_name: Type.Optional(Type.String()),
  reviewer_photo_url: Type.Optional(Type.String()),
  review_created_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'review_created_at' } }),
  ),
  reply_comment: Type.Optional(Type.String()),
  reply_updated_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'reply_updated_at' } }),
  ),
  replied_via_auto: Type.Boolean(),
  suggested_reply: Type.Optional(Type.String()),
  status: Type.Enum(GoogleReviewStatus),
});
export type GoogleReviewResponseObject = Static<typeof GoogleReviewResponseSchema>;

export const GoogleReviewsListResponseSchema = Type.Object({
  total: Type.Number(),
  items: Type.Array(GoogleReviewResponseSchema),
  // Accès à la génération IA (capability `marketing.ai_review_reply`, incluse au plan Max). Pilote le teaser frontend.
  has_ai_review_reply: Type.Boolean(),
});
export type GoogleReviewsListResponseObject = Static<typeof GoogleReviewsListResponseSchema>;

export const GoogleReviewSuggestionResponseSchema = Type.Object({
  google_review_id: Type.Number(),
  suggested_reply: Type.String(),
});
export type GoogleReviewSuggestionResponseObject = Static<typeof GoogleReviewSuggestionResponseSchema>;

export const GoogleReviewReplyRequestSchema = Type.Object({
  comment: Type.String({ minLength: 1, maxLength: 4096, title: 'Réponse à publier' }),
});
export type GoogleReviewReplyRequestObject = Static<typeof GoogleReviewReplyRequestSchema>;

export const ProReputationParamsResponseSchema = Type.Object({
  auto_reply_enabled: Type.Boolean(),
  reply_tone_instructions: Type.Optional(Type.String()),
});
export type ProReputationParamsResponseObject = Static<typeof ProReputationParamsResponseSchema>;

export const UpsertProReputationParamsRequestSchema = Type.Object({
  auto_reply_enabled: Type.Boolean({
    placeholder: 'INPUT_PLACEHOLDER_REVIEW_AUTO_REPLY',
  }),
  reply_tone_instructions: Type.Optional(
    Type.String({
      title: 'INPUT_TITLE_REVIEW_REPLY_TONE',
      placeholder: 'INPUT_PLACEHOLDER_REVIEW_REPLY_TONE',
      maxLength: 1000,
      meta: { multiline: true },
    }),
  ),
});
export type UpsertProReputationParamsRequestObject = Static<typeof UpsertProReputationParamsRequestSchema>;
